export interface Country {
  iso: string;
  name: { en: string; ru: string };
  flag: string;
  dial: string;
  mask: string;
  minDigits: number;
  maxDigits: number;
}

// `mask` is the NATIONAL portion only (no country code prefix) — the dial code
// is shown by the country selector next to the input, so duplicating it in the
// input itself would look broken.
export const COUNTRIES: Country[] = [
  { iso: "ru", name: { en: "Russia", ru: "Россия" }, flag: "🇷🇺", dial: "+7", mask: "(###) ###-##-##", minDigits: 11, maxDigits: 11 },
  { iso: "kz", name: { en: "Kazakhstan", ru: "Казахстан" }, flag: "🇰🇿", dial: "+7", mask: "(###) ###-##-##", minDigits: 11, maxDigits: 11 },
  { iso: "by", name: { en: "Belarus", ru: "Беларусь" }, flag: "🇧🇾", dial: "+375", mask: "(##) ###-##-##", minDigits: 12, maxDigits: 12 },
  { iso: "ua", name: { en: "Ukraine", ru: "Украина" }, flag: "🇺🇦", dial: "+380", mask: "(##) ###-##-##", minDigits: 12, maxDigits: 12 },
  { iso: "tm", name: { en: "Turkmenistan", ru: "Туркменистан" }, flag: "🇹🇲", dial: "+993", mask: "(##) ##-##-##", minDigits: 11, maxDigits: 11 },
  { iso: "uz", name: { en: "Uzbekistan", ru: "Узбекистан" }, flag: "🇺🇿", dial: "+998", mask: "(##) ###-##-##", minDigits: 12, maxDigits: 12 },
  { iso: "kg", name: { en: "Kyrgyzstan", ru: "Киргизия" }, flag: "🇰🇬", dial: "+996", mask: "(###) ###-###", minDigits: 12, maxDigits: 12 },
  { iso: "ge", name: { en: "Georgia", ru: "Грузия" }, flag: "🇬🇪", dial: "+995", mask: "(###) ##-##-##", minDigits: 12, maxDigits: 12 },
  { iso: "am", name: { en: "Armenia", ru: "Армения" }, flag: "🇦🇲", dial: "+374", mask: "(##) ###-###", minDigits: 11, maxDigits: 11 },
  { iso: "az", name: { en: "Azerbaijan", ru: "Азербайджан" }, flag: "🇦🇿", dial: "+994", mask: "(##) ###-##-##", minDigits: 12, maxDigits: 12 },
  { iso: "us", name: { en: "United States", ru: "США" }, flag: "🇺🇸", dial: "+1", mask: "(###) ###-####", minDigits: 11, maxDigits: 11 },
  { iso: "gb", name: { en: "United Kingdom", ru: "Великобритания" }, flag: "🇬🇧", dial: "+44", mask: "#### ######", minDigits: 12, maxDigits: 12 },
  { iso: "de", name: { en: "Germany", ru: "Германия" }, flag: "🇩🇪", dial: "+49", mask: "### ########", minDigits: 10, maxDigits: 13 },
  { iso: "tr", name: { en: "Turkey", ru: "Турция" }, flag: "🇹🇷", dial: "+90", mask: "(###) ### ## ##", minDigits: 12, maxDigits: 12 },
  { iso: "pl", name: { en: "Poland", ru: "Польша" }, flag: "🇵🇱", dial: "+48", mask: "### ### ###", minDigits: 11, maxDigits: 11 },
];

const ISO_INDEX: Record<string, Country> = Object.fromEntries(COUNTRIES.map((c) => [c.iso, c]));

export function getCountry(iso: string): Country | undefined {
  return ISO_INDEX[iso];
}

export function digitsOnly(s: string): string {
  return (s.match(/\d+/g) || []).join("");
}

function dialDigits(c: Country): string {
  return digitsOnly(c.dial);
}

// applyMask receives ONLY the national digits (no country code).
// Country code is embedded as literal text in the mask itself.
export function applyMask(natDigits: string, mask: string): string {
  if (natDigits.length === 0) return "";
  let out = "";
  let di = 0;
  for (const ch of mask) {
    if (ch === "#") {
      if (di >= natDigits.length) return out;
      out += natDigits[di++];
    } else {
      out += ch;
    }
  }
  return out;
}

function nationalDigits(raw: string, c: Country): string {
  const code = dialDigits(c);
  let d = digitsOnly(raw);
  if (d.startsWith(code)) d = d.slice(code.length);
  return d.slice(0, c.maxDigits - code.length);
}

export function formatPhone(raw: string, c: Country): string {
  return applyMask(nationalDigits(raw, c), c.mask);
}

export function switchCountry(raw: string, from: Country, to: Country): string {
  const fromCode = dialDigits(from);
  let d = digitsOnly(raw);
  if (d.startsWith(fromCode)) d = d.slice(fromCode.length);
  d = d.slice(0, to.maxDigits - dialDigits(to).length);
  return applyMask(d, to.mask);
}

export interface PhoneInvalid {
  kind: "wrong-length";
  expectedNational: number;
  gotNational: number;
}

export function diagnosePhone(raw: string, c: Country): PhoneInvalid | null {
  const natD = nationalDigits(raw, c);
  const codeLen = dialDigits(c).length;
  const minNat = c.minDigits - codeLen;
  const maxNat = c.maxDigits - codeLen;
  if (natD.length < minNat || natD.length > maxNat) {
    return { kind: "wrong-length", expectedNational: maxNat, gotNational: natD.length };
  }
  return null;
}

export function toE164(rawNational: string, c: Country): string {
  const natD = nationalDigits(rawNational, c);
  return `${c.dial} ${applyMask(natD, c.mask)}`.trim();
}

export function isValidForCountry(raw: string, c: Country): boolean {
  return diagnosePhone(raw, c) === null;
}

export function placeholderFor(mask: string): string {
  return mask.replace(/#/g, "_");
}

export function detectCountry(): Country {
  try {
    const nav = (navigator.language || "").toUpperCase();
    const region = nav.split("-")[1] || "";
    const byRegion = COUNTRIES.find((c) => c.iso.toUpperCase() === region);
    if (byRegion) return byRegion;
    if (nav.startsWith("RU")) return ISO_INDEX.ru;
    if (nav.startsWith("EN")) return ISO_INDEX.us;
  } catch {
    // ignore
  }
  return ISO_INDEX.ru;
}
